"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useToast,
  Text,
} from "@chakra-ui/react";
import { Users } from "lucide-react";
import {
  AgentSwapToolFields,
  defaultAgentSwapValues,
  type AgentSwapFormValues,
} from "./AgentSwapToolFields.tsx";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface AgentTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: Record<string, unknown>) => void;
  initialValues?: Record<string, unknown>;
  currentAgentId?: string;
}

export const AgentTransferModal: React.FC<AgentTransferModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
  currentAgentId,
}) => {
  const [values, setValues] = useState<AgentSwapFormValues>(
    defaultAgentSwapValues()
  );
  const [nameError, setNameError] = useState("");
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const modalBg = isDark ? "gray.800" : "white";
  const modalFg = isDark ? "white" : "gray.800";
  const labelColor = isDark ? "gray.200" : "gray.700";
  const helperMuted = isDark ? "gray.400" : "gray.600";
  const subtleBorder = isDark ? "gray.600" : "gray.200";
  const subtleBg = isDark ? "gray.700" : "gray.50";
  const dividerColor = isDark ? "gray.600" : "gray.200";

  const inputProps = {
    bg: isDark ? "gray.700" : "white",
    borderColor: isDark ? "gray.600" : "gray.300",
    color: modalFg,
    _placeholder: { color: helperMuted },
    _focus: {
      borderColor: isDark ? "gray.300" : "gray.800",
      boxShadow: isDark ? "none" : undefined,
    },
  } as const;

  useEffect(() => {
    if (!initialValues || initialValues.type !== "agent_swap") {
      setValues(defaultAgentSwapValues());
      return;
    }
    const v = initialValues;
    setValues({
      name: (v.name as string) || "agent_transfer",
      description: (v.description as string) || "",
      agent_id: (v.agent_id as string) || "",
      keep_current_voice: !!v.keep_current_voice,
      keep_current_language: !!v.keep_current_language,
      post_call_analysis_setting:
        (v.post_call_analysis_setting as AgentSwapFormValues["post_call_analysis_setting"]) ||
        "only_destination_agent",
      webhook_setting:
        (v.webhook_setting as AgentSwapFormValues["webhook_setting"]) ||
        "only_source_agent",
      speak_during_execution: !!v.speak_during_execution,
      execution_message_type:
        (v.execution_message_type as "prompt" | "static_text") || "prompt",
      execution_message_description:
        (v.execution_message_description as string) || "",
    });
  }, [initialValues]);

  const validateToolName = (val: string): { ok: boolean; message?: string } => {
    if (!val.trim()) {
      const message = "Tool name cannot be empty";
      setNameError(message);
      return { ok: false, message };
    }
    if (val.length > 64) {
      const message = "Tool name must be 64 characters or less";
      setNameError(message);
      return { ok: false, message };
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(val)) {
      const message =
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)";
      setNameError(message);
      return { ok: false, message };
    }
    setNameError("");
    return { ok: true };
  };

  const patchValues = (patch: Partial<AgentSwapFormValues>) => {
    if (patch.name !== undefined) setNameError("");
    setValues((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = () => {
    const nameCheck = validateToolName(values.name);
    if (!nameCheck.ok) {
      toast({
        title: "Invalid tool name",
        description: nameCheck.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!values.agent_id.trim()) {
      toast({
        title: "Select an agent",
        description: "Choose the agent to transfer the call to.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const config: Record<string, unknown> = {
      type: "agent_swap",
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      agent_id: values.agent_id.trim(),
      keep_current_voice: values.keep_current_voice,
      keep_current_language: values.keep_current_language,
      post_call_analysis_setting: values.post_call_analysis_setting,
      webhook_setting: values.webhook_setting,
      speak_during_execution: values.speak_during_execution,
    };

    if (values.speak_during_execution) {
      config.execution_message_type = values.execution_message_type;
      config.execution_message_description =
        values.execution_message_description.trim();
    }

    onSave(config);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={modalBg} color={modalFg} maxH="90vh">
        <ModalHeader display="flex" alignItems="center" gap={2}>
          <Users size={22} />
          <Text as="span">Agent Transfer</Text>
        </ModalHeader>

        <ModalBody>
          <AgentSwapToolFields
            value={values}
            onChange={patchValues}
            excludeAgentId={currentAgentId}
            nameError={nameError}
            onNameBlur={(name) => {
              validateToolName(name);
            }}
            inputProps={inputProps}
            labelColor={labelColor}
            helperMuted={helperMuted}
            subtleBorder={subtleBorder}
            subtleBg={subtleBg}
            showIntro
          />
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={dividerColor}>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
